const TAU = Math.PI * 2;

export const DEFAULT_TUNNEL_CONFIG = {
  farZ: -104,
  nearZ: -6.4,
  recycleZ: -5.8,
  minLaneRadius: 6.8,
  maxLaneRadius: 15.8,
  minSpeed: 7.8,
  maxSpeed: 12.2,
  loopSpeed: 9.6,
  loopDepthJitter: 0.08,
  planeScaleMultiplier: 1.16,
  recycleDepthStart: 0.015,
  recycleDepthSpan: 0.22,
  recycleDepthSlots: 36,
  farOpacity: 0.035,
  introBoostMultiplier: 1,
  introBoostDuration: 1.2,
  introCameraStartZ: 54,
  introCameraDuration: 1.35,
  introLoopRampStart: 0.65,
  introLoopRampDuration: 0.75,
  sectors: 18,
  candidateAttempts: 18,
  candidateStride: 997,
  minProjectedDistance: 0.075,
  minProjectedEdgeGap: 0.035,
  projectedPlaneAspect: 1,
  initialDepthStart: 0.02,
  initialDepthSpan: 0.88,
  lazyIntroDepthStart: 0.16,
  lazyIntroDepthSpan: 0.28,
  seed: 'floema-tunnel',
};

export function createTunnelSlot(index = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const random = createRandom(`${config.seed}:${index}`);
  const sectorCount = Math.max(5, Math.floor(config.sectors));
  const sectorWidth = TAU / sectorCount;
  const cycle = Math.floor(index / sectorCount);
  const sector = getSectorForIndex(index, sectorCount, config.seed, cycle);
  const angle = normalizeAngle(
    (sector + 0.5) * sectorWidth +
      randomBetween(random, -sectorWidth * 0.34, sectorWidth * 0.34)
  );
  const radiusBias = 0.22 + Math.pow(random(), 0.82) * 0.78;
  const radius =
    config.minLaneRadius + (config.maxLaneRadius - config.minLaneRadius) * radiusBias;
  const z = randomBetween(random, config.farZ, config.farZ * 0.28);
  const speed = Number.isFinite(config.loopSpeed)
    ? config.loopSpeed
    : randomBetween(random, config.minSpeed, config.maxSpeed);
  const scale =
    randomBetween(random, 0.88, 1.72) * config.planeScaleMultiplier;

  return {
    x: round(Math.cos(angle) * radius),
    y: round(Math.sin(angle) * radius * randomBetween(random, 0.84, 1.08)),
    z: round(z),
    speed: round(speed),
    baseScale: round(scale),
    rotationZ: 0,
    rotationSpeed: 0,
    laneAngle: round(angle),
    sectorIndex: sector,
  };
}

export function createSeparatedTunnelSlot(index = 0, occupiedSlots = [], options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const attempts = Math.max(1, Math.floor(config.candidateAttempts));
  let bestSlot = null;
  let bestScore = -Infinity;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const candidate = createTunnelSlot(
      index + attempt * config.candidateStride,
      config
    );
    candidate.projectedPlaneAspect = getSlotProjectedPlaneAspect(candidate, config);

    if (Number.isFinite(config.fixedZ)) {
      candidate.z = round(config.fixedZ);
    }

    const distance = getNearestProjectedDistance(candidate, occupiedSlots, config);
    const edgeGap = getNearestProjectedEdgeGap(candidate, occupiedSlots, config);
    const score = getSeparationScore(distance, edgeGap, config);

    if (score > bestScore) {
      bestSlot = candidate;
      bestScore = score;
    }

    if (
      distance >= config.minProjectedDistance &&
      edgeGap >= config.minProjectedEdgeGap
    ) {
      return candidate;
    }
  }

  return bestSlot;
}

export function getProjectedTunnelDistance(a, b, options = {}) {
  const first = projectTunnelSlot(a, options);
  const second = projectTunnelSlot(b, options);

  return Math.hypot(first.x - second.x, first.y - second.y);
}

export function getTunnelIntroZ(depthProgress, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const start = options.lazy ? config.lazyIntroDepthStart : config.initialDepthStart;
  const span = options.lazy ? config.lazyIntroDepthSpan : config.initialDepthSpan;
  const visibleRange = config.nearZ - config.farZ;

  return round(config.farZ + visibleRange * (start + clamp(depthProgress, 0, 1) * span));
}

export function getLoopedTunnelDepthProgress(index = 0, total = 1, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const count = Math.max(1, Math.floor(total));
  const random = createRandom(`${config.seed}:loop-depth:${index}`);
  const jitter = (random() - 0.5) * config.loopDepthJitter;

  return normalizeUnit((index + 0.5 + jitter) / count);
}

export function updateTunnelSlot(slot, deltaSeconds, options = {}) {
  const speedMultiplier = options.speedMultiplier ?? 1;
  slot.z = round(slot.z + slot.speed * deltaSeconds * speedMultiplier);
  slot.rotationZ = round(slot.rotationZ + slot.rotationSpeed * deltaSeconds * speedMultiplier);
  return slot;
}

export function advanceTunnelLoopOffset(offset = 0, deltaSeconds = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const speedMultiplier = options.speedMultiplier ?? 1;
  const depthRange = Math.max(0.001, getTunnelLoopEndZ(config) - config.farZ);

  return offset + (config.loopSpeed * deltaSeconds * speedMultiplier) / depthRange;
}

export function getContinuousTunnelProgress(phase = 0, offset = 0) {
  return normalizeUnit(phase + offset);
}

export function getContinuousTunnelCycle(phase = 0, offset = 0) {
  return Math.floor(phase + offset);
}

export function getContinuousTunnelZ(progress = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };

  return round(config.farZ + (getTunnelLoopEndZ(config) - config.farZ) * clamp(progress, 0, 1));
}

export function getIntroSpeedMultiplier(elapsedSeconds = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const duration = Math.max(0.001, config.introBoostDuration);
  const boost = Math.max(1, config.introBoostMultiplier);
  const release = smoothstep(0, duration, elapsedSeconds);

  return round(1 + (boost - 1) * (1 - release));
}

export function getIntroCameraZ(elapsedSeconds = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const duration = Math.max(0.001, config.introCameraDuration);
  const startZ = Math.max(0, config.introCameraStartZ);
  const release = smootherstep(0, duration, elapsedSeconds);

  return round(startZ * (1 - release));
}

export function getIntroLoopSpeedMultiplier(elapsedSeconds = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const rampStart = Math.max(0, config.introLoopRampStart);
  const rampDuration = Math.max(0.001, config.introLoopRampDuration);

  if (elapsedSeconds < rampStart) {
    return 0;
  }

  return round(smoothstep(rampStart, rampStart + rampDuration, elapsedSeconds));
}

export function shouldRecycleTunnelSlot(slot, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  return slot.z >= config.recycleZ;
}

export function getRecycleTunnelZ(cycle = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const visibleRange = config.nearZ - config.farZ;
  const slots = Math.max(1, Math.floor(config.recycleDepthSlots));
  const slotProgress = ((cycle % slots) + 0.5) / slots;
  const progress = config.recycleDepthStart + slotProgress * config.recycleDepthSpan;

  return round(config.farZ + visibleRange * progress);
}

export function recycleTunnelSlot(slot, index = 0, cycle = 0, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const next = createTunnelSlot(index + cycle * 997, {
    ...config,
    farZ: config.farZ,
  });

  Object.assign(slot, next, {
    z: getRecycleTunnelZ(cycle, config),
  });

  return slot;
}

export function getTunnelVisualState(slot, options = {}) {
  const config = { ...DEFAULT_TUNNEL_CONFIG, ...options };
  const progress = clamp((slot.z - config.farZ) / (config.nearZ - config.farZ), 0, 1);
  const fadeIn = smoothstep(0.04, 0.5, progress);
  const opacity = clamp(
    config.farOpacity + (1 - config.farOpacity) * fadeIn,
    config.farOpacity,
    1
  );
  const contrast = clamp(0.7 + progress * 0.45, 0.7, 1.08);
  const scaleMultiplier = clamp(0.62 + progress * 0.78, 0.62, 1.36);

  return {
    progress,
    opacity: round(opacity),
    contrast: round(contrast),
    scaleMultiplier: round(scaleMultiplier),
  };
}

export function getStratifiedDepthProgress(index = 0, options = {}) {
  const bands = Math.max(2, Math.floor(options.bands ?? 10));
  const seed = options.seed ?? DEFAULT_TUNNEL_CONFIG.seed;
  const cycle = Math.floor(index / bands);
  const band = getSectorForIndex(index, bands, `${seed}:depth`, cycle);
  const random = createRandom(`${seed}:depth-jitter:${index}`);
  const jitter = randomBetween(random, 0.12, 0.88);

  return (band + jitter) / bands;
}

function createRandom(seed) {
  let state = hashString(seed);

  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function getNearestProjectedDistance(slot, occupiedSlots, options) {
  if (occupiedSlots.length === 0) {
    return Infinity;
  }

  return occupiedSlots.reduce(
    (minDistance, occupiedSlot) =>
      Math.min(minDistance, getProjectedTunnelDistance(slot, occupiedSlot, options)),
    Infinity
  );
}

function getNearestProjectedEdgeGap(slot, occupiedSlots, options) {
  if (occupiedSlots.length === 0) {
    return Infinity;
  }

  return occupiedSlots.reduce(
    (minGap, occupiedSlot) =>
      Math.min(minGap, getProjectedTunnelEdgeGap(slot, occupiedSlot, options)),
    Infinity
  );
}

function getProjectedTunnelEdgeGap(a, b, options = {}) {
  const first = projectTunnelSlotBounds(a, options);
  const second = projectTunnelSlotBounds(b, options);
  const gapX = Math.abs(first.x - second.x) - first.halfWidth - second.halfWidth;
  const gapY =
    Math.abs(first.y - second.y) - first.halfHeight - second.halfHeight;

  return Math.max(gapX, gapY);
}

function getSeparationScore(distance, edgeGap, options) {
  const minDistance = Math.max(0, options.minProjectedDistance ?? 0);
  const minEdgeGap = Math.max(0, options.minProjectedEdgeGap ?? 0);
  const distanceScore = Number.isFinite(distance)
    ? Math.min(distance, minDistance * 2 || distance)
    : distance;
  const edgeScore = Number.isFinite(edgeGap)
    ? Math.min(edgeGap, minEdgeGap * 2 || edgeGap)
    : edgeGap;

  return edgeScore * 4 + distanceScore;
}

function projectTunnelSlot(slot, options = {}) {
  const bounds = projectTunnelSlotBounds(slot, options);

  return {
    x: bounds.x,
    y: bounds.y,
  };
}

function projectTunnelSlotBounds(slot, options = {}) {
  const fov = options.fov ?? 50;
  const aspect = options.cameraAspect ?? options.aspect ?? 1;
  const planeAspect = getSlotProjectedPlaneAspect(slot, options);
  const visual = getTunnelVisualState(slot, options);
  const scale = slot.baseScale * visual.scaleMultiplier;
  const depth = Math.max(0.2, -slot.z);
  const halfViewportHeight = depth * Math.tan((fov * Math.PI) / 360);
  const halfViewportWidth = halfViewportHeight * aspect;

  return {
    x: slot.x / halfViewportWidth,
    y: slot.y / halfViewportHeight,
    halfWidth: (scale * planeAspect * 0.5) / halfViewportWidth,
    halfHeight: (scale * 0.5) / halfViewportHeight,
  };
}

function getSlotProjectedPlaneAspect(slot, options = {}) {
  return Math.max(
    0.2,
    toFiniteNumber(
      slot?.projectedPlaneAspect,
      toFiniteNumber(options.projectedPlaneAspect, 1)
    )
  );
}

function getTunnelLoopEndZ(config) {
  return Number.isFinite(config.loopEndZ) ? config.loopEndZ : config.recycleZ;
}

function hashString(value) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function randomBetween(random, min, max) {
  return min + (max - min) * random();
}

function getSectorForIndex(index, sectorCount, seed, cycle) {
  return createSectorOrder(sectorCount, seed, cycle)[index % sectorCount];
}

function createSectorOrder(sectorCount, seed, cycle) {
  let bestOrder = null;
  let bestScore = Infinity;

  for (let attempt = 0; attempt < 16; attempt += 1) {
    const sectors = Array.from({ length: sectorCount }, (_, sector) => sector);
    const random = createRandom(`${seed}:sectors:${cycle}:${attempt}`);

    for (let current = sectors.length - 1; current > 0; current -= 1) {
      const target = Math.floor(random() * (current + 1));
      [sectors[current], sectors[target]] = [sectors[target], sectors[current]];
    }

    const score = scoreSectorOrder(sectors, sectorCount);

    if (score < bestScore) {
      bestOrder = sectors;
      bestScore = score;
    }
  }

  return bestOrder;
}

function scoreSectorOrder(sectors, sectorCount) {
  const sectorWidth = TAU / sectorCount;
  const goldenAngle = 2.399963229728653;
  let score = 0;

  for (let index = 1; index < sectors.length; index += 1) {
    const diff = angularDistance(
      sectors[index] * sectorWidth,
      sectors[index - 1] * sectorWidth
    );

    if (Math.abs(diff - goldenAngle) < 0.38) {
      score += 3;
    }

    if (diff < sectorWidth * 1.35) {
      score += 1.4;
    }
  }

  return score;
}

function normalizeAngle(angle) {
  return ((angle % TAU) + TAU) % TAU;
}

function angularDistance(a, b) {
  const diff = Math.abs(normalizeAngle(a - b));
  return Math.min(diff, TAU - diff);
}

function smoothstep(edge0, edge1, value) {
  const x = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return x * x * (3 - 2 * x);
}

function smootherstep(edge0, edge1, value) {
  const x = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return x * x * x * (x * (x * 6 - 15) + 10);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeUnit(value) {
  return ((value % 1) + 1) % 1;
}

function toFiniteNumber(value, fallback) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function round(value) {
  return Math.round(value * 10000) / 10000;
}
